import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useWishlist } from '../contexts/WishlistContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Heart, ShoppingCart, Package, ArrowLeft, Star, Trash2 } from 'lucide-react';
import { needsChoices, displayPrice } from '../lib/cartLine';
import PriceTag from '../components/PriceTag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WishlistPage() {
  const { user } = useAuth();
  const { wishlistIds, toggleWishlist } = useWishlist();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    axios.get(`${API}/wishlist`, { withCredentials: true })
      .then(r => setProducts(r.data.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [user, wishlistIds.length]);

  const handleAddToCart = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (needsChoices(product)) { navigate(`/products/${productId}`); return; }
    await addToCart(productId, 1);
  };

  const handleRemove = async (productId) => {
    await toggleWishlist(productId);
    setProducts(prev => prev.filter(p => p.id !== productId));
  };

  if (!user) return (
    <div className="store-page relative">
      <Navbar />
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[60vh] text-center px-6 pt-8">
        <Heart className="w-16 h-16 text-ink/15 mb-6" />
        <h1 className="text-2xl font-black text-ink mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Wishlist</h1>
        <p className="text-ink/55 mb-6">Log in to save and view your wishlist.</p>
        <Link to="/auth" className="btn-primary px-6 py-3 text-sm font-bold">Log in</Link>
      </div>
    </div>
  );

  return (
    <div className="store-page relative">
      <Navbar />
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-8 pb-20">
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate(-1)} className="text-ink/55 hover:text-ink transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Wishlist
            </h1>
            <p className="text-ink/55 text-sm">{products.length} saved item{products.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-honey border-t-transparent rounded-full animate-spin" /></div>
        ) : products.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-center py-20">
            <Heart className="w-16 h-16 text-ink/15 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-ink mb-3">No saved items yet</h2>
            <p className="text-ink/55 mb-6 text-sm">Heart any product to save it here.</p>
            <Link to="/" className="btn-primary px-6 py-3 text-sm font-bold">Browse Products</Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <AnimatePresence>
              {products.map((product, i) => (
                <motion.div key={product.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: i * 0.04 }}
                  className="glass-card rounded-2xl overflow-hidden group"
                  data-testid={`wishlist-item-${product.id}`}
                >
                  <Link to={`/products/${product.id}`} className="block relative aspect-square overflow-hidden">
                    {product.images?.[0] ? (
                      <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-ink/[0.04]">
                        <Package className="w-10 h-10 text-ink/30" />
                      </div>
                    )}
                    {product.compare_price && (
                      <span className="absolute top-3 left-3 bg-honey text-black text-xs font-bold px-2 py-0.5 rounded-full">
                        -{Math.round(((product.compare_price - product.price) / product.compare_price) * 100)}%
                      </span>
                    )}
                  </Link>
                  <div className="p-4">
                    <Link to={`/products/${product.id}`}>
                      <h3 className="text-ink font-bold text-sm line-clamp-2 hover:text-honey-700 transition-colors mb-1">{product.title}</h3>
                    </Link>
                    {product.avg_rating > 0 && (
                      <div className="flex items-center gap-1 mb-2">
                        <Star className="w-3 h-3 fill-honey text-honey-600" />
                        <span className="text-ink/55 text-xs">{product.avg_rating} ({product.review_count})</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-3">
                      <PriceTag {...displayPrice(product)} className="text-sm" />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleAddToCart(product.id)}
                        data-testid={`wishlist-add-cart-${product.id}`}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-honey/10 text-honey-700 border border-honey/20 rounded-xl text-xs font-bold hover:bg-honey/20 transition-colors">
                        <ShoppingCart className="w-3.5 h-3.5" /> Add to Cart
                      </button>
                      <button onClick={() => handleRemove(product.id)}
                        data-testid={`wishlist-remove-${product.id}`}
                        className="p-2 bg-red-500/10 text-red-600 rounded-xl hover:bg-red-500/20 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
